import StatusChip from 'components/@extended/StatusChip';
import { PackageIcon, ShieldIcon } from 'components/icons';
import { Switch } from 'components/ui/switch';
import { useSetBackOfficeSettingEnabledMutation } from 'store/api/back-office-settings';
import type { SettingListElement } from 'types/back-office-settings';
import { cn } from 'utils/cn';
import { t, translations } from 'utils/getTranslations';
import { SettingCaution } from './SettingCaution';
import type { SettingRowLayout } from './settingLayout';
import { SettingSubOptions } from './SettingSubOptions';
import { useSettingSave } from './useSettingSave';
import { WhoHasItPopover } from './WhoHasItPopover';

// No border: the design draws none, and the badge's transparent one makes a wrapping chip 2px taller than its 22px.
export const CHIP_CLASS = 'gap-[5px] rounded-[6px] border-0';

interface Props {
    setting: SettingListElement;
    layout: SettingRowLayout;
    /** Without UPDATE.BACK_OFFICE_SETTINGS: drawn dimmed, every control off. */
    readOnly: boolean;
}

/**
 * One setting: label, description, its chips and caution, and while on its sub-options and who has it. The switch
 * saves at once; a failed save flips it back.
 */
export function SettingRow({ setting, layout, readOnly }: Props) {
    const [setEnabled] = useSetBackOfficeSettingEnabledMutation();
    const save = useSettingSave();
    const copy = `back-office-settings.${setting.key}`;
    const labelId = `setting-${setting.key}-label`;
    const directions = (['SHOWS', 'HIDES'] as const).flatMap((direction) => {
        const codes = setting.governedPrivileges.filter((privilege) => privilege.direction === direction);
        return codes.length ? [{ direction, codes: codes.map(({ code }) => code).join(', ') }] : [];
    });

    return (
        <div
            className={cn(
                'border-divider new:border-divider-soft flex items-start gap-4 border-t px-5 py-3.5',
                // On a phone the label shares its line with the switch alone, and the rest takes the full width.
                'max-sm:grid max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:gap-x-3 max-sm:gap-y-1',
                readOnly && 'opacity-60'
            )}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-1 max-sm:contents">
                <div id={labelId} className="text-fg text-sm leading-5 font-medium">
                    {t(`${copy}.label`)}
                </div>
                <p className="text-grey-600 m-0 max-w-[680px] text-[13px] leading-[19px] max-sm:col-span-2">
                    {t(`${copy}.description`)}
                </p>
                {setting.paidFlag || directions.length ? (
                    <div className="mt-1 flex flex-wrap gap-1.5 max-sm:col-span-2">
                        {setting.paidFlag ? (
                            <StatusChip
                                size="compact"
                                className={CHIP_CLASS}
                                icon={<PackageIcon className="size-3" />}
                                label={t('back-office-settings.only-while-module', {
                                    module: t(`back-office-settings.modules.${setting.paidFlag}`),
                                })}
                            />
                        ) : null}
                        {directions.map(({ direction, codes }) => (
                            <StatusChip
                                key={direction}
                                size="compact"
                                className={cn(CHIP_CLASS, 'h-auto min-h-5.5 whitespace-normal')}
                                icon={<ShieldIcon className="size-3" />}
                                label={
                                    <span>
                                        {t(`back-office-settings.governed-${direction.toLowerCase()}`)}{' '}
                                        {/* A privilege code has no break point of its own: on a phone it breaks anywhere. */}
                                        <span className="new:text-caption font-mono text-[11.5px] font-normal max-sm:wrap-anywhere">
                                            {codes}
                                        </span>
                                    </span>
                                }
                            />
                        ))}
                    </div>
                ) : null}
                {`${copy}.caution` in translations ? (
                    <SettingCaution text={t(`${copy}.caution`)} className="mt-1 max-w-[660px] max-sm:col-span-2" />
                ) : null}
                {setting.enabled && layout.sub?.length ? (
                    <SettingSubOptions setting={setting} sub={layout.sub} readOnly={readOnly} />
                ) : null}
            </div>
            <div className="flex min-h-5.5 shrink-0 items-center gap-3 max-sm:contents">
                {setting.enabled ? (
                    <div className="flex max-sm:col-span-2 max-sm:justify-self-start">
                        <WhoHasItPopover setting={setting} readOnly={readOnly} />
                    </div>
                ) : null}
                <Switch
                    className="max-sm:col-start-2 max-sm:row-start-1"
                    aria-labelledby={labelId}
                    checked={setting.enabled}
                    disabled={readOnly}
                    onCheckedChange={(enabled) => void save(() => setEnabled({ key: setting.key, enabled }).unwrap())}
                />
            </div>
        </div>
    );
}
